import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { fetchPersonnelAssets, PersonnelAssetAct, PersonnelAssets } from "services/personnel.service";
import { panelClass } from "./PersonnelCardParts";
import { formatDay } from "./personnel-helpers";

const chipClass = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const thClass = "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
const tdClass = "px-3 py-2 align-top text-sm text-gray-700";
// Dropped on a phone, where the table does not fit; the asset's type and serial move under its name.
const hiddenOnPhone = "hidden md:table-cell";

function TypeAndSerialOnPhone({ type, serial }: { type: string | null; serial: string | null }) {
  const text = [type, serial].filter(Boolean).join(" · ");
  return text ? <div className="text-xs text-gray-500 md:hidden">{text}</div> : null;
}

/** 'in use' / 'in stock' → i18n key; anything else is shown as sent. */
function assetStatusLabel(status: string | null, t: (key: string, opts?: any) => string): string {
  if (!status) return "";
  const key = `personnel_asset_status_${status.replace(/\s+/g, "_")}`;
  return t(key, { defaultValue: status });
}

function ActChip({ act }: { act: PersonnelAssetAct | null }) {
  const { t } = useTranslation();
  if (!act?.status) return null;
  const tone =
    act.status === "signed"
      ? "bg-green-50 text-green-800"
      : act.status === "annulled"
        ? "bg-gray-100 text-gray-600"
        : "bg-amber-50 text-amber-800";
  return (
    <span className={clsx(chipClass, tone)}>
      {t(`personnel_asset_act_${act.status}`, { defaultValue: act.status })}
    </span>
  );
}

function AssetName({
  companyId,
  assetId,
  name,
  canOpen,
}: {
  companyId: string;
  assetId: string | null;
  name: string;
  canOpen: boolean;
}) {
  const label = name || "—";
  return canOpen && assetId ? (
    <Link to={`/app/${companyId}/assets/${assetId}`} className="font-medium text-blue-700 hover:underline max-md:inline-block max-md:py-1.5">
      {label}
    </Link>
  ) : (
    <span className="font-medium text-gray-900">{label}</span>
  );
}

const brandModel = (brand: string, model: string) => [brand, model].filter(Boolean).join(" ");

/**
 * «Turtas»: what is issued to the person now + issue/return history,
 * read-only. Names link into the Assets module only when the backend says
 * the viewer may open it (`canOpenAssets`: Master / 'Access assets').
 */
export default function PersonnelAssetsTab({ companyId, userId }: { companyId: string; userId: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<PersonnelAssets | null>(null);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestRef.current;
    setFailed(false);
    fetchPersonnelAssets(companyId, userId)
      .then((result) => {
        if (requestId === requestRef.current) setData(result);
      })
      .catch(() => {
        if (requestId === requestRef.current) setFailed(true);
      });
  }, [companyId, userId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  if (failed) {
    return (
      <div className={clsx(panelClass, "flex items-center gap-3 text-sm text-red-700")}>
        {t("personnel_assets_load_failed")}
        <button type="button" className="underline" onClick={load}>
          {t("retry")}
        </button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-gray-500">{t("loading")}</p>;

  const { current, history, canOpenAssets } = data;

  return (
    <div className="space-y-4">
      <section className={panelClass}>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("personnel_assets_current")}</h2>
        {current.length === 0 ? (
          <p className="text-sm text-gray-500">{t("personnel_assets_none")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr>
                  <th className={thClass}>{t("personnel_asset_name")}</th>
                  <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_asset_type")}</th>
                  <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_asset_serial")}</th>
                  <th className={thClass}>{t("personnel_asset_issued")}</th>
                  <th className={thClass}>{t("status")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {current.map((asset) => (
                  <tr key={asset.id}>
                    <td className={tdClass}>
                      <AssetName companyId={companyId} assetId={asset.id} name={asset.name} canOpen={canOpenAssets} />
                      {brandModel(asset.brand, asset.model) && (
                        <div className="text-xs text-gray-500">{brandModel(asset.brand, asset.model)}</div>
                      )}
                      <TypeAndSerialOnPhone type={asset.typeTitle} serial={asset.serial} />
                    </td>
                    <td className={clsx(tdClass, hiddenOnPhone)}>{asset.typeTitle || "—"}</td>
                    <td className={clsx(tdClass, hiddenOnPhone)}>{asset.serial || "—"}</td>
                    <td className={tdClass}>{formatDay(asset.issuedDate) || "—"}</td>
                    <td className={tdClass}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {asset.status && <span>{assetStatusLabel(asset.status, t)}</span>}
                        {asset.isBroken && (
                          <span className={clsx(chipClass, "bg-red-50 text-red-700")}>{t("personnel_asset_broken")}</span>
                        )}
                        <ActChip act={asset.assignAct} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={panelClass}>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("personnel_assets_history")}</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">{t("personnel_assets_history_none")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr>
                  <th className={thClass}>{t("personnel_asset_name")}</th>
                  <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_asset_type")}</th>
                  <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_asset_serial")}</th>
                  <th className={thClass}>{t("personnel_asset_issued")}</th>
                  <th className={thClass}>{t("personnel_asset_returned")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((row) => (
                  <tr key={row.id}>
                    <td className={tdClass}>
                      <AssetName companyId={companyId} assetId={row.assetId} name={row.name} canOpen={canOpenAssets} />
                      {brandModel(row.brand, row.model) && (
                        <div className="text-xs text-gray-500">{brandModel(row.brand, row.model)}</div>
                      )}
                      <TypeAndSerialOnPhone type={row.typeTitle} serial={row.serial} />
                    </td>
                    <td className={clsx(tdClass, hiddenOnPhone)}>{row.typeTitle || "—"}</td>
                    <td className={clsx(tdClass, hiddenOnPhone)}>{row.serial || "—"}</td>
                    <td className={tdClass}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span>{formatDay(row.issuedDate) || "—"}</span>
                        <ActChip act={row.assignAct} />
                      </div>
                    </td>
                    <td className={tdClass}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {row.returnedDate ? (
                          <span>{formatDay(row.returnedDate)}</span>
                        ) : row.isCurrent ? (
                          <span className={clsx(chipClass, "bg-blue-50 text-blue-800")}>
                            {t("personnel_asset_still_issued")}
                          </span>
                        ) : (
                          <span>—</span>
                        )}
                        <ActChip act={row.returnAct} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
